import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Dialog } from '@/components/ui/dialog';
import { apiErrorMessage } from '@shared/api/client';
import {
  useDeleteWalletDocument,
  useUpdateWalletDocument,
  useUploadWalletDocument,
  useWalletDocuments,
} from '@shared/hooks/use-wallet-documents';
import type { WalletDocumentResponse } from '@shared/types/api';
import { WalletCategorySelect } from './wallet-category-select';

const CATEGORIES = ['ALL', 'AADHAAR', 'PAN', 'LICENCE', 'PASSPORT', 'BANK', 'OTHER'] as const;

export function DocumentWalletPage() {
  const navigate = useNavigate();
  const [selectedCategory, setSelectedCategory] = useState<string>('ALL');
  const [searchQuery, setSearchQuery] = useState('');

  const {
    data: documents = [],
    isLoading,
    isError,
    error,
  } = useWalletDocuments({
    category: selectedCategory,
    search: searchQuery,
  });
  const uploadMutation = useUploadWalletDocument();
  const updateMutation = useUpdateWalletDocument();
  const deleteMutation = useDeleteWalletDocument();

  // Upload Modal state
  const [uploadOpen, setUploadOpen] = useState(false);
  const [uploadFile, setUploadFile] = useState<File | null>(null);
  const [uploadName, setUploadName] = useState('');
  const [uploadCategory, setUploadCategory] = useState('AADHAAR');
  const [uploadError, setUploadError] = useState<string | null>(null);

  // Edit Modal state
  const [editDoc, setEditDoc] = useState<WalletDocumentResponse | null>(null);
  const [editName, setEditName] = useState('');
  const [editCategory, setEditCategory] = useState('');
  const [editFile, setEditFile] = useState<File | null>(null);
  const [editError, setEditError] = useState<string | null>(null);

  // Delete Modal state
  const [deleteDoc, setDeleteDoc] = useState<WalletDocumentResponse | null>(null);

  const filteredDocuments = documents;

  function handleOpenUpload() {
    setUploadFile(null);
    setUploadName('');
    setUploadCategory('AADHAAR');
    setUploadError(null);
    setUploadOpen(true);
  }

  function handleFileSelect(file: File | null) {
    if (!file) return;
    setUploadFile(file);
  }

  async function handleUploadSubmit(e: React.FormEvent) {
    e.preventDefault();
    setUploadError(null);

    if (!uploadFile) {
      setUploadError('Please select a document file to upload');
      return;
    }

    const isOther = uploadCategory.toUpperCase() === 'OTHER';
    if (isOther && !uploadName.trim()) {
      setUploadError('Document name is required when category is Other Document');
      return;
    }

    try {
      await uploadMutation.mutateAsync({
        file: uploadFile,
        documentName: uploadName.trim() || undefined,
        documentType: uploadCategory,
      });
      setUploadOpen(false);
    } catch (err) {
      setUploadError(apiErrorMessage(err, 'Failed to upload document to wallet'));
    }
  }

  function handleOpenEdit(doc: WalletDocumentResponse) {
    setEditDoc(doc);
    setEditName(doc.documentName);
    setEditCategory(doc.documentType || 'OTHER');
    setEditFile(null);
    setEditError(null);
  }

  async function handleEditSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!editDoc) return;
    setEditError(null);

    if (!editName.trim()) {
      setEditError('Document name cannot be empty');
      return;
    }

    try {
      await updateMutation.mutateAsync({
        id: editDoc.id,
        documentName: editName.trim(),
        documentType: editCategory,
        file: editFile || undefined,
      });
      setEditDoc(null);
    } catch (err) {
      setEditError(apiErrorMessage(err, 'Failed to update document'));
    }
  }

  async function handleDeleteConfirm() {
    if (!deleteDoc) return;
    try {
      await deleteMutation.mutateAsync(deleteDoc.id);
      setDeleteDoc(null);
    } catch (err) {
      alert(apiErrorMessage(err, 'Failed to delete document'));
    }
  }

  return (
    <div className="w-full px-4 py-6 sm:px-6 pb-24">
      {/* Back to Menu Button (Mobile Only) */}
      <button
        type="button"
        onClick={() => navigate('/client/menu')}
        className="md:hidden inline-flex items-center gap-1.5 mb-4 text-xs font-bold text-gray-700 hover:text-brand-800 bg-white border border-gray-200 hover:border-brand-300 px-3 py-1.5 rounded-xl shadow-xs transition-all active:scale-95 cursor-pointer select-none"
      >
        <svg
          className="w-4 h-4 text-brand-700"
          fill="none"
          stroke="currentColor"
          strokeWidth="2.5"
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 19.5L8.25 12l7.5-7.5" />
        </svg>
        <span>Back to Menu</span>
      </button>

      {/* Header Card */}
      <div className="rounded-2xl border border-gray-200 bg-gradient-to-br from-brand-50/50 via-white to-white p-6 sm:p-8 shadow-sm select-none">
        <h1 className="text-2xl font-bold text-gray-900 sm:text-2xl">Document Wallet</h1>
        <p className="mt-1.5 text-base text-gray-600 max-w-2xl leading-relaxed sm:text-sm">
          Upload & safely store your identity cards, licenses & essential documents. Easily send
          them into service chats whenever requested!
        </p>
      </div>

      {/* Filter and Search Bar */}
      <div className="mt-6 flex flex-col md:flex-row md:items-center justify-between gap-4 bg-white p-4 rounded-2xl border border-gray-200 shadow-xs">
        {/* Category Tabs */}
        <div className="flex items-center gap-1.5 overflow-x-auto pb-1 md:pb-0 scrollbar-none">
          {CATEGORIES.map((cat) => (
            <button
              key={cat}
              type="button"
              onClick={() => setSelectedCategory(cat)}
              className={`px-3.5 py-1.5 rounded-xl text-xs font-bold transition-all shrink-0 ${
                selectedCategory === cat
                  ? 'bg-brand-700 text-white shadow-xs'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              {cat === 'ALL' ? 'All Documents' : cat}
            </button>
          ))}
        </div>

        {/* Search Input */}
        <div className="relative min-w-[240px]">
          <input
            type="text"
            placeholder="Search documents by name..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-9 pr-4 py-2 text-xs sm:text-sm rounded-xl border border-gray-300 focus:border-brand-600 focus:outline-none bg-gray-50 focus:bg-white transition-colors"
          />
          <svg
            className="w-4 h-4 text-gray-400 absolute left-3 top-2.5"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
            />
          </svg>
        </div>
      </div>

      {/* Document Grid */}
      {isLoading ? (
        <div className="mt-12 text-center py-12 bg-white rounded-3xl border border-gray-150 shadow-xs">
          <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-brand-700 border-r-transparent"></div>
          <p className="mt-3 text-sm text-gray-500 font-medium">Loading your Document Wallet…</p>
        </div>
      ) : isError ? (
        <div className="mt-8 rounded-2xl bg-red-50 border border-red-200 p-6 text-center text-red-700">
          <p className="text-sm font-semibold">Failed to load wallet documents</p>
          <p className="text-xs mt-1 text-red-600">{apiErrorMessage(error)}</p>
        </div>
      ) : filteredDocuments.length === 0 ? (
        <div className="mt-8 text-center py-16 px-4 bg-white rounded-3xl border border-dashed border-gray-300 shadow-xs">
          <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-2xl bg-brand-50 text-brand-700 mb-4">
            <svg
              className="w-8 h-8"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.75"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m2.25 0H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z"
              />
            </svg>
          </div>
          <h3 className="text-base font-bold text-gray-900 sm:text-lg">
            {searchQuery || selectedCategory !== 'ALL'
              ? 'No matching documents found'
              : 'Your Document Wallet is empty'}
          </h3>
          <p className="mt-1 text-xs sm:text-sm text-gray-500 max-w-md mx-auto">
            {searchQuery || selectedCategory !== 'ALL'
              ? 'Try changing your search terms or category filter.'
              : 'Upload your Aadhaar card, PAN card, driving licence or official files here to keep them organized.'}
          </p>
          <Button
            onClick={handleOpenUpload}
            className="mt-6 bg-brand-700 hover:bg-brand-800 text-white font-bold px-5 py-2.5 rounded-xl shadow-sm text-xs sm:text-sm"
          >
            + Upload Document Now
          </Button>
        </div>
      ) : (
        <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {/* Add New Document Grid Card */}
          <button
            type="button"
            onClick={handleOpenUpload}
            className="flex flex-col items-center justify-center p-6 min-h-[160px] rounded-2xl border-2 border-dashed border-gray-300 hover:border-brand-600 bg-gray-50/50 hover:bg-brand-50/40 text-gray-500 hover:text-brand-700 transition-all duration-200 cursor-pointer text-center group shadow-xs hover:shadow-sm"
          >
            <div className="w-12 h-12 rounded-full bg-white group-hover:bg-brand-100 flex items-center justify-center border border-gray-200 group-hover:border-brand-300 text-gray-400 group-hover:text-brand-700 transition-colors mb-2">
              <svg
                className="w-6 h-6"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.5"
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 4.5v15m7.5-7.5h-15" />
              </svg>
            </div>
            <span className="text-sm font-bold text-gray-800 group-hover:text-brand-800">
              Add New Document
            </span>
            <span className="text-xs text-gray-500 mt-0.5">Click to upload another document</span>
          </button>

          {filteredDocuments.map((doc) => (
            <DocumentCard
              key={doc.id}
              doc={doc}
              onEdit={() => handleOpenEdit(doc)}
              onDelete={() => setDeleteDoc(doc)}
            />
          ))}
        </div>
      )}

      {/* Upload Modal */}
      <Dialog
        open={uploadOpen}
        onClose={() => setUploadOpen(false)}
        title="Upload to Document Wallet"
        footer={null}
      >
        <form onSubmit={handleUploadSubmit} className="space-y-4 py-2">
          {uploadError ? (
            <div className="rounded-xl bg-red-50 border border-red-200 p-3 text-xs text-red-700 font-medium">
              {uploadError}
            </div>
          ) : null}

          <div>
            <label className="block text-xs font-bold uppercase tracking-wider text-gray-700 mb-1">
              Select Document File / Image *
            </label>
            <div className="flex flex-col items-center justify-center border-2 border-dashed border-gray-300 hover:border-brand-500 rounded-2xl p-5 bg-gray-50/50 transition-colors cursor-pointer text-center relative">
              <input
                type="file"
                className="absolute inset-0 opacity-0 cursor-pointer w-full h-full"
                onChange={(e) => handleFileSelect(e.target.files?.[0] || null)}
                accept="image/*,.pdf,.doc,.docx"
              />
              <svg
                className="w-8 h-8 text-brand-600 mb-2"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.75"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M12 16.5V9.75m0 0l3 3m-3-3l-3 3M6.75 19.5h10.5a2.25 2.25 0 002.25-2.25V6.75a2.25 2.25 0 00-2.25-2.25H6.75A2.25 2.25 0 004.5 6.75v10.5a2.25 2.25 0 002.25 2.25z"
                />
              </svg>
              {uploadFile ? (
                <div className="text-xs">
                  <span className="font-bold text-gray-900 block truncate max-w-xs">
                    {uploadFile.name}
                  </span>
                  <span className="text-gray-500">({(uploadFile.size / 1024).toFixed(1)} KB)</span>
                </div>
              ) : (
                <div className="text-xs text-gray-500">
                  <span className="font-bold text-brand-700 hover:underline">Click to upload</span>{' '}
                  or drag and drop file here
                  <p className="text-[10px] text-gray-400 mt-1">
                    Supports Images (JPG, PNG), PDF, DOCX up to 50MB
                  </p>
                </div>
              )}
            </div>
          </div>

          <div>
            <label className="block text-xs font-bold uppercase tracking-wider text-gray-700 mb-1">
              Category
            </label>
            <WalletCategorySelect value={uploadCategory} onChange={setUploadCategory} />
          </div>

          <div>
            <label className="block text-xs font-bold uppercase tracking-wider text-gray-700 mb-1">
              Document Name / Identifier{' '}
              {uploadCategory.toUpperCase() === 'OTHER' ? (
                <span className="text-red-500">*</span>
              ) : (
                <span className="text-gray-400 font-normal lowercase">(optional)</span>
              )}
            </label>
            <input
              type="text"
              required={uploadCategory.toUpperCase() === 'OTHER'}
              placeholder={
                uploadCategory.toUpperCase() === 'OTHER'
                  ? 'e.g. Electric Bill, Rent Agreement, Trade Mark'
                  : 'Enter custom name (optional)'
              }
              value={uploadName}
              onChange={(e) => setUploadName(e.target.value)}
              className="w-full px-3.5 py-2.5 text-sm rounded-xl border border-gray-300 focus:border-brand-600 focus:outline-none bg-white shadow-xs font-medium text-gray-900"
            />
          </div>

          <div className="mt-6 flex items-center justify-end gap-2 pt-2 border-t border-gray-100">
            <Button
              type="button"
              variant="ghost"
              onClick={() => setUploadOpen(false)}
              className="text-xs font-bold"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={uploadMutation.isPending}
              className="bg-brand-700 hover:bg-brand-800 text-white font-bold text-xs px-5 py-2.5 rounded-xl"
            >
              {uploadMutation.isPending ? 'Saving Document…' : 'Save to Wallet'}
            </Button>
          </div>
        </form>
      </Dialog>

      {/* Edit Modal */}
      <Dialog
        open={!!editDoc}
        onClose={() => setEditDoc(null)}
        title="Edit Wallet Document"
        footer={null}
      >
        <form onSubmit={handleEditSubmit} className="space-y-4 py-2">
          {editError ? (
            <div className="rounded-xl bg-red-50 border border-red-200 p-3 text-xs text-red-700 font-medium">
              {editError}
            </div>
          ) : null}

          <div>
            <label className="block text-xs font-bold uppercase tracking-wider text-gray-700 mb-1">
              Category
            </label>
            <WalletCategorySelect value={editCategory} onChange={setEditCategory} />
          </div>

          <div>
            <label className="block text-xs font-bold uppercase tracking-wider text-gray-700 mb-1">
              Document Name / Identifier{' '}
              {editCategory.toUpperCase() === 'OTHER' ? (
                <span className="text-red-500">*</span>
              ) : (
                <span className="text-gray-400 font-normal lowercase">(optional)</span>
              )}
            </label>
            <input
              type="text"
              required={editCategory.toUpperCase() === 'OTHER'}
              value={editName}
              onChange={(e) => setEditName(e.target.value)}
              className="w-full px-3.5 py-2.5 text-sm rounded-xl border border-gray-300 focus:border-brand-600 focus:outline-none bg-white shadow-xs font-medium text-gray-900"
            />
          </div>

          <div>
            <label className="block text-xs font-bold uppercase tracking-wider text-gray-700 mb-1">
              Replace File (Optional)
            </label>
            <input
              type="file"
              onChange={(e) => setEditFile(e.target.files?.[0] || null)}
              className="block w-full text-xs text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-xl file:border-0 file:text-xs file:font-semibold file:bg-brand-50 file:text-brand-700 hover:file:bg-brand-100"
            />
            {editDoc ? (
              <p className="mt-1 text-[11px] text-gray-400">Current file: {editDoc.fileName}</p>
            ) : null}
          </div>

          <div className="mt-6 flex items-center justify-end gap-2 pt-2 border-t border-gray-100">
            <Button
              type="button"
              variant="ghost"
              onClick={() => setEditDoc(null)}
              className="text-xs font-bold"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={updateMutation.isPending}
              className="bg-brand-700 hover:bg-brand-800 text-white font-bold text-xs px-5 py-2.5 rounded-xl"
            >
              {updateMutation.isPending ? 'Updating…' : 'Save Changes'}
            </Button>
          </div>
        </form>
      </Dialog>

      {/* Delete Confirmation Modal */}
      <Dialog
        open={!!deleteDoc}
        onClose={() => setDeleteDoc(null)}
        title="Delete Document"
        footer={
          <>
            <Button variant="ghost" onClick={() => setDeleteDoc(null)}>
              Cancel
            </Button>
            <Button
              variant="outline"
              className="border-red-300 text-red-600 hover:bg-red-50"
              disabled={deleteMutation.isPending}
              onClick={handleDeleteConfirm}
            >
              {deleteMutation.isPending ? 'Deleting…' : 'Delete Document'}
            </Button>
          </>
        }
      >
        <p className="text-sm text-gray-600">
          Are you sure you want to remove{' '}
          <strong className="text-gray-900">{deleteDoc?.documentName}</strong> from your wallet?
        </p>
      </Dialog>
    </div>
  );
}

function DocumentCard({
  doc,
  onEdit,
  onDelete,
}: {
  doc: WalletDocumentResponse;
  onEdit: () => void;
  onDelete: () => void;
}) {
  const isImage = doc.mimeType.startsWith('image/');
  const sizeKb = (Number(doc.sizeBytes) / 1024).toFixed(1);

  return (
    <div className="flex flex-col justify-between rounded-2xl border border-gray-200 bg-white p-4 shadow-xs hover:shadow-md transition-all duration-200 group">
      <div>
        {/* Top bar: category tag + actions */}
        <div className="flex items-center justify-between gap-2">
          <span className="inline-flex items-center rounded-md bg-brand-50 px-2.5 py-0.5 text-[10px] font-extrabold uppercase tracking-wide text-brand-800 border border-brand-200/60">
            {doc.documentType || 'DOCUMENT'}
          </span>
          <div className="flex items-center gap-1 opacity-80 group-hover:opacity-100 transition-opacity">
            <button
              type="button"
              onClick={onEdit}
              className="p-1 text-gray-400 hover:text-brand-700 rounded-md hover:bg-gray-100"
              title="Edit Document"
            >
              <svg
                className="w-4 h-4"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M16.862 4.487l1.687-1.688a1.875 1.875 0 112.652 2.652L10.582 16.07a4.5 4.5 0 01-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 011.13-1.897l8.932-8.931zm0 0L19.5 7.125M18 14v4.75A2.25 2.25 0 0115.75 21H5.25A2.25 2.25 0 013 18.75V8.25A2.25 2.25 0 015.25 6H10"
                />
              </svg>
            </button>
            <button
              type="button"
              onClick={onDelete}
              className="p-1 text-gray-400 hover:text-red-600 rounded-md hover:bg-gray-100"
              title="Delete Document"
            >
              <svg
                className="w-4 h-4"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M14.74 9l-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 01-2.244 2.077H8.084a2.25 2.25 0 01-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 00-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 013.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 00-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 00-7.5 0"
                />
              </svg>
            </button>
          </div>
        </div>

        {/* Thumbnail / Icon & Document Title */}
        <div className="mt-3.5 flex items-start gap-3">
          <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-gray-100 overflow-hidden border border-gray-200 text-gray-500">
            {isImage && doc.downloadUrl ? (
              <img src={doc.downloadUrl} alt="" className="h-full w-full object-cover" />
            ) : (
              <svg
                className="w-6 h-6 text-brand-700"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.75"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m0 12.75h7.5m-7.5-3h7.5M8.25 6H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z"
                />
              </svg>
            )}
          </div>
          <div className="min-w-0 flex-1">
            <h4 className="text-sm font-extrabold text-gray-900 truncate leading-snug">
              {doc.documentName}
            </h4>
            <p className="text-xs text-gray-500 truncate mt-0.5">{doc.fileName}</p>
            <p className="text-[11px] text-gray-400 mt-1 font-medium">
              {sizeKb} KB •{' '}
              {new Date(doc.createdAt).toLocaleDateString([], {
                month: 'short',
                day: 'numeric',
                year: 'numeric',
              })}
            </p>
          </div>
        </div>
      </div>

      {/* Footer Action */}
      <div className="mt-4 pt-3 border-t border-gray-100 flex items-center justify-between">
        {doc.downloadUrl ? (
          <a
            href={doc.downloadUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1.5 text-xs font-bold text-brand-700 hover:text-brand-900 hover:underline"
          >
            <svg
              className="w-3.5 h-3.5"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.2"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M2.036 12.322a1.012 1.012 0 010-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178z"
              />
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
              />
            </svg>
            View Document
          </a>
        ) : (
          <span className="text-xs text-gray-400 italic">No URL available</span>
        )}
      </div>
    </div>
  );
}
